import React, { useEffect, useState } from "react";
import "./Navbar.css";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  FaBars,
  FaUser,
  FaCalendarAlt,
  FaPassport,
  FaComments,
  FaShieldAlt,
  FaChevronDown,
  FaExclamationTriangle,
  FaSignOutAlt,
  FaBell,
  FaHotel,
  FaWalking,
  FaClipboardList,
  FaWallet,
  FaChartLine,
  FaUserCog,
  FaArrowLeft,
  FaUserFriends,
  FaBook
} from "react-icons/fa";
import CurrencyService from "../services/currencyService";
import Swal from "sweetalert2";
import logo from "../assets/logo.png";
import logoLeft from "../assets/logo-left.png";
import { getApiBaseUrl, getBackendBaseUrl } from "../utils/apiConfig";
import { getFileUrl } from "../utils/imageUrl";

function NavUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const BACKEND_URL = getBackendBaseUrl();
  const API_URL = getApiBaseUrl();

  // Check if we are on the explorer home page
  const isHomePage = location.pathname.startsWith("/travelerhome") || location.pathname === "/explore" || location.pathname === "/home" || location.pathname === "/dashboard";

  const [showSidebar, setShowSidebar] = useState(false);
  const [agentStatus, setAgentStatus] = useState(null);
  const [hotelStatus, setHotelStatus] = useState(null);
  const [guideStatus, setGuideStatus] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(CurrencyService.selectedCurrency);
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [profileData, setProfileData] = useState(null);

  let user = null;
  try {
    const rawUser = localStorage.getItem("user");
    if (rawUser && rawUser !== "undefined" && rawUser !== "null") {
      user = JSON.parse(rawUser);
    }
  } catch (e) {
    user = null;
  }

  /* ================= FETCH PROFILE FOR AVATAR ================= */
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("jwt_token");
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!res.ok) {
          if (res.status === 401 || res.status === 403 || res.status === 404) {
            localStorage.removeItem("jwt_token");
            localStorage.removeItem("user");
            setProfileData(null);
          }
          return;
        }

        const data = await res.json();
        setProfileData(data);
      } catch (err) {
        console.log(err);
      }
    };
    fetchProfile();
  }, [API_URL, navigate]);

  // Use profileData role if available, otherwise fallback to localStorage
  const currentRole = profileData?.role_id || user?.role_id;
  const currentUserStatusId = profileData?.status_id || user?.status_id;
  const currentUserStatusName = profileData?.Status?.name || profileData?.status?.name;

  const isApprovedStatus = (sId, sName) => {
    return sId === 4 || sId === 5 || sName === "Approved" || sName === "Active";
  };

  const isApprovedHotelOwner = hotelStatus === 4 || ((currentRole === 3 || currentRole === 1) && isApprovedStatus(currentUserStatusId, currentUserStatusName));
  const isApprovedGuide = guideStatus === 4 || (currentRole === 2 && isApprovedStatus(currentUserStatusId, currentUserStatusName));
  const isApprovedAgent = agentStatus === 4 || (currentRole === 6 && isApprovedStatus(currentUserStatusId, currentUserStatusName));

  const isPendingHotelOwner = !isApprovedHotelOwner && (hotelStatus === 3 || hotelStatus === 1 || currentRole === 3 || currentRole === 1);
  const isPendingGuide = !isApprovedGuide && (guideStatus === 3 || guideStatus === 1 || currentRole === 2);
  const isPendingAgent = !isApprovedAgent && (agentStatus === 3 || agentStatus === 1 || currentRole === 6);

  /* ================= ROLE STATUSES ================= */
  useEffect(() => {
    const fetchStatuses = async () => {
      // Only Travelers (role 5, or legacy 2) can apply for new roles
      if (currentRole && currentRole !== 5 && currentRole !== 2) return;
      const token = localStorage.getItem("jwt_token");
      if (!token) return;

      try {
        // Agent Status
        const agentRes = await fetch(`${API_URL}/agents/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (agentRes.ok) {
          const agentData = await agentRes.json().catch(() => ({}));
          if (agentData && agentData.status) setAgentStatus(agentData.status);
        }

        // Hotel Status
        const hotelRes = await fetch(`${API_URL}/traveler-to-hotel-owner/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (hotelRes.ok) {
          const hotelData = await hotelRes.json().catch(() => ({}));
          if (hotelData && hotelData.status) setHotelStatus(hotelData.status);
        }

        // Guide Status
        const guideRes = await fetch(`${API_URL}/traveler-to-guide/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (guideRes.ok) {
          const guideData = await guideRes.json().catch(() => ({}));
          if (guideData && guideData.status) setGuideStatus(guideData.status);
        }
      } catch (err) {
        console.log("Error fetching statuses:", err);
      }
    };
    fetchStatuses();
  }, [user?.id, currentRole, API_URL]);

  /* ================= PROFILE NAVIGATION ================= */
  const handleProfileClick = () => {
    if (!user) {
      alert("Please log in first!");
      navigate("/login");
      return;
    }

    navigate("/profile");

    setShowSidebar(false);
  };

  /* ================= PROFESSIONAL PARTNER ROUTES ================= */
  const handleBecomeHotelOwner = () => {
    navigate("/become-hotel-owner");
    setShowSidebar(false);
  };

  const handleBecomeGuide = () => {
    navigate("/become-guide");
    setShowSidebar(false);
  };

  const handleBecomeAgent = () => {
    navigate("/become-agent");
    setShowSidebar(false);
  };

  /* ================= LOGOUT ================= */
  const handleLogout = () => {
    localStorage.clear();
    setShowSidebar(false);
    navigate("/login");
  };

  const handleCurrencyChange = (currencyCode) => {
    CurrencyService.selectedCurrency = currencyCode;
    setSelectedCurrency(currencyCode); // ✅ correct setter
    setShowCurrencyMenu(false);
  };

  const handleInviteFriends = () => {
    navigate("/invite");
    setShowSidebar(false);
  };


  return (
    <>
      {/* ================= HEADER ================= */}
      <header className="swx-header">
        <nav className="swx-nav-left swx-nav-control">
          {isHomePage ? (
            <Link to="/explore" className="swx-logo-link" title="Soloway">
              <img src={logoLeft} alt="Soloway" className="left-logo-img" />
            </Link>
          ) : (
            <button className="swx-back-btn" onClick={() => navigate(-1)} title="Go Back">
              <FaArrowLeft />
            </button>
          )}
        </nav>

        <Link to="/explore" className="swx-logo" title="Soloway">
          <img src={logo} alt="Soloway - Explore Sri Lanka Differently" className="logo-img" />
        </Link>

        <nav className="swx-nav-right swx-nav-control">
          <button className="swx-menu-icon" onClick={() => setShowSidebar(true)} aria-label="Open Menu">
            <FaBars />
          </button>
        </nav>
      </header>

      {/* ================= BACKDROP ================= */}
      {showSidebar && (
        <div className="swx-sidebar-backdrop" onClick={() => setShowSidebar(false)} />
      )}

      {/* ================= SIDEBAR ================= */}
      <aside className={`swx-sidebar ${showSidebar ? "open" : ""}`}>

        {/* ===== USER INFO HEADER ===== */}
        {localStorage.getItem("jwt_token") ? (
          <div className="swx-sidebar-user">
            <div className="swx-sidebar-avatar" style={{ marginRight: '15px' }}>
              {profileData?.logo ? (
                <img 
                  src={getFileUrl(profileData.logo, BACKEND_URL)} 
                  alt="Profile" 
                  className="swx-avatar-img" 
                  style={{ width: '45px', height: '45px', borderRadius: '50%', objectFit: 'cover', border: '2px solid white' }} 
                />
              ) : (
                <div className="swx-avatar-placeholder" style={{ width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaUser color="#999" />
                </div>
              )}
            </div>
            <div className="swx-user-info-text">
              <div className="swx-username">{profileData?.full_name || user?.full_name || "Traveler"}</div>
              <div className="swx-user-email">{profileData?.email || user?.email || "user@example.com"}</div>
            </div>
          </div>
        ) : (
          <div className="swx-sidebar-user" style={{ paddingBottom: '20px', borderBottom: '1px solid #eee', marginBottom: '10px' }}>
            <div className="swx-user-info-text">
              <div className="swx-username">Guest Explorer</div>
              <div className="swx-user-email">Please log in to unlock features</div>
            </div>
          </div>
        )}

        {/* ===== MENU ITEMS ===== */}
        <button onClick={handleProfileClick}><FaUser /> My Profile</button>
        <button onClick={() => {
          // Feature under development: navigate("/events");
          Swal.fire({
            title: "Feature Under Development",
            text: "The Events feature is currently under development and will be available soon!",
            icon: "info",
            confirmButtonColor: "#0284c7"
          });
        }}><FaCalendarAlt /> Events</button>
        <button onClick={() => {
          // Feature under development: navigate("/visa");
          Swal.fire({
            title: "Feature Under Development",
            text: "The Visa Apply feature is currently under development and will be available soon!",
            icon: "info",
            confirmButtonColor: "#0284c7"
          });
        }}><FaPassport /> Visa Apply</button>
        <button onClick={() => navigate("/messages")}><FaComments /> Chat</button>
        <button onClick={() => navigate("/insurance")}><FaShieldAlt /> Travel Insurance</button>
        <button onClick={() => navigate("/emergency")}><FaExclamationTriangle /> Emergency SOS</button>
        <button onClick={() => navigate("/notifications")}><FaBell /> Notifications</button>
        
        <div className="swx-divider" style={{ margin: '10px 0', borderTop: '1px solid #eee' }} />
        
        {/* ===== OTHER SERVICES SECTION ===== */}
        <div className="swx-role-section">
          <div className="swx-section-label" style={{ padding: '8px 15px', fontSize: '12px', color: '#888', fontWeight: 'bold' }}>OTHER SERVICES</div>
          <button onClick={() => navigate("/rewards")}><FaWallet /> My Coins</button>
          <button onClick={handleInviteFriends}><FaUserFriends /> Invite Friends</button>
          <button onClick={() => navigate("/bookings")}><FaClipboardList /> Booking History</button>
          <button onClick={() => { navigate("/policies"); setShowSidebar(false); }}><FaBook /> Soloway Policies</button>
        </div>

        <div className="swx-divider" style={{ margin: '10px 0', borderTop: '1px solid #eee' }} />

        {/* ===== HOTEL OWNER SECTION ===== */}
        {isApprovedHotelOwner && (
          <div className="swx-role-section">
            <div className="swx-section-label" style={{ padding: '8px 15px', fontSize: '12px', color: '#888', fontWeight: 'bold' }}>HOTEL MANAGEMENT</div>

            <button onClick={() => navigate("/manage/hotel-bookings")}><FaClipboardList /> Hotel Bookings</button>
            <button onClick={() => navigate("/manage/hotel-settings")}><FaUserCog /> Hotel Settings</button>
          </div>
        )}

        {/* ===== GUIDE SECTION ===== */}
        {isApprovedGuide && (
          <div className="swx-role-section">
            <div className="swx-section-label" style={{ padding: '8px 15px', fontSize: '12px', color: '#888', fontWeight: 'bold' }}>GUIDE MANAGEMENT</div>

            <button onClick={() => navigate("/manage/tour-bookings")}><FaClipboardList /> Tour Bookings</button>
            <button onClick={() => navigate("/manage/guide-analytics")}><FaChartLine /> Booking Summary</button>
            <button onClick={() => navigate("/manage/guide-settings")}><FaUserCog /> Guide Settings</button>
          </div>
        )}

        {/* ===== AGENT SECTION ===== */}
        {isApprovedAgent && (
          <div className="swx-role-section">
            <div className="swx-section-label" style={{ padding: '8px 15px', fontSize: '12px', color: '#888', fontWeight: 'bold' }}>AGENT MANAGEMENT</div>

            <button onClick={() => navigate("/partner/commissions")}><FaWallet /> My Commission</button>
            <button onClick={() => navigate(`/profile?tab=agent`)}><FaChartLine /> Agent Dashboard</button>
          </div>
        )}

        {/* ===== BECOME SELLER / PENDING SECTION ===== */}
        {(currentRole === 5 || isPendingHotelOwner || isPendingGuide || isPendingAgent || (!isApprovedHotelOwner && !isApprovedGuide && !isApprovedAgent)) && (
          <div className="swx-role-section">
            <div className="swx-section-label" style={{ padding: '8px 15px', fontSize: '12px', color: '#888', fontWeight: 'bold' }}>
              {isPendingHotelOwner || isPendingGuide || isPendingAgent ? "ACCOUNT STATUS" : "GROW YOUR BUSINESS"}
            </div>
            
            {!isPendingHotelOwner && !isPendingGuide && !isPendingAgent && !isApprovedHotelOwner && !isApprovedGuide && !isApprovedAgent && (
              <>
                <button onClick={handleBecomeHotelOwner}><FaHotel /> Become Hotel Owner</button>
                <button onClick={handleBecomeGuide}><FaWalking /> Become Guide</button>
                <button onClick={handleBecomeAgent}><FaUserFriends /> Become Agent</button>
              </>
            )}

            {isPendingHotelOwner && (
              <button className="swx-disabled" disabled><FaHotel /> Waiting for approval for hotel owner</button>
            )}

            {isPendingGuide && (
              <button className="swx-disabled" disabled><FaWalking /> Waiting for approval for guide</button>
            )}

            {isPendingAgent && (
              <button className="swx-disabled" disabled><FaUserFriends /> Waiting for approval for agent</button>
            )}
          </div>
        )}

        {/* ===== CURRENCY DROPDOWN ===== */}
        <button className="swx-currency-toggle" onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}>
          Currency: {selectedCurrency} <FaChevronDown className={showCurrencyMenu ? "rotate" : ""} />
        </button>
        {showCurrencyMenu && (
          <div className="swx-currency-submenu">
            <button onClick={() => handleCurrencyChange("USD")}> USD</button>
            <button onClick={() => handleCurrencyChange("LKR")}> LKR</button>
          </div>
        )}


        {/* ===== LOGOUT (BOTTOM) ===== */}
        <div className="swx-sidebar-footer">
          {localStorage.getItem("jwt_token") ? (
            <button className="swx-logout-btn" onClick={handleLogout}>
              <FaSignOutAlt /> Logout
            </button>
          ) : (
            <button className="swx-logout-btn" onClick={() => navigate("/login")} style={{ color: '#3b82f6', background: 'transparent' }}>
              <FaUser /> Login / Sign Up
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

export default NavUser;
